import { select } from '@angular-redux/store';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    Renderer2,
    ViewContainerRef
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BaseComponent } from "sharedFeatures/base";
import { NewPlanStartFormBodyComponent } from "sharedFeatures/newplan-win";
import { SetupWarningPopup } from "sharedFeatures/newplan-win/setup-warning-popup";
import { AuthData, BrixxPlan, NewPlanDto } from "sharedFeatures/sharedModels";
import { NewPlanDataValues } from "sharedFeatures/sharedModels/newPlanDataValues";
import { AccountsGroup } from 'sharedFeatures/sharedModels/xero/accountsGroupModel';
import { AccountsModel } from 'sharedFeatures/sharedModels/xero/accountsModel';
import { PlanService, RedirectService, XeroService } from "sharedFeatures/sharedServices";
import { ICreatePlanResponse, IErrorResponse, isSuccessResponse } from "sharedFeatures/sharedServices/Responses/plan.index";
import { DateUtils } from 'sharedFeatures/utils/date-utils';
import { PopupComponent, WindowComponent } from "sharedFeatures/window";
import { SharedGuiSmallModule } from '../../../../sharedGuiSmall/sharedGuiSmall.module';
import { NewPlanFormBodyComponent } from './newplan-body.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'newplan-form',
    templateUrl: '../window/window.component.html',
    styleUrls: ['../window/window.component.less'],
    standalone: true,
    imports: [SharedGuiSmallModule, TranslateModule],
    providers: [PlanService, RedirectService, XeroService]
})
export class NewPlanForm extends WindowComponent {

    public static amPLAccounts = 1;
    public static amBSAccounts = 2;

    formContentBody: any = NewPlanStartFormBodyComponent;

    // Unsubscribe event
    private storeUnsubscribe = new Subject<void>();

    protected planGuid: string = '';

    @Input() authData: AuthData = new AuthData();
    @Input() plans: BrixxPlan[] = [];
    @Input() companyId: number = 0;

    @Output() onCOASaved = new EventEmitter<boolean>();

    private resizeUnlisten: Function | undefined;

    cornerRadius = 0;

    data: NewPlanDataValues = new NewPlanDataValues();

    aVatText: string = 'off';

    durationYear: string = "1";

    yearEndMonth: string = "1";

    startYearString: string = '';
    monthString: string = '';

    actualsStartYearString: string = '';
    actualsMonthString: string = '';

    currencyString: string = "1";

    planCreateInProgress: boolean = false;

    newPlan: NewPlanDto = new NewPlanDto();
    @Input() isShowClose: boolean = true;
    
    @select(['appData', 'appComponent'])
    readonly appComponent$!: Observable<any>;
    appComponent: any = null;

    @select(['appData', 'settingsForm'])
    readonly settingsForm$!: Observable<any>;
    private settingsForm: any = null;

    @select(['xeroSettings', 'plAccounts'])
    readonly plAccounts$!: Observable<AccountsModel>;
    plAccountsData?: AccountsModel;

    @select(['xeroSettings', 'bsAccounts'])
    readonly bsAccounts$!: Observable<AccountsModel>;
    bsAccountsData?: AccountsModel;

    @Input()
    renderForSignup = false;

    @Input()
    disableScroll = false;

    constructor(element: ElementRef,
        r: Renderer2,
        viewContainerRef: ViewContainerRef,
        chDet: ChangeDetectorRef,
        private planService: PlanService,
        private reDirectService: RedirectService,
        private xeroService: XeroService
    ) {

        super(element, r, viewContainerRef, chDet);

        this.resizeUnlisten = this.r.listen(window, 'resize', ev => this.handleWindowResize(ev));

        this.width = 420;
        this.height = 364;

        this.windowContentBody = NewPlanFormBodyComponent;

    }

    // Ivy has bug, event is raised multiply times if inherited component has any @HostListener
    //@HostListener('window:resize', ['$event'])
    handleWindowResize(ev: UIEvent): void {
        if (!this.renderForSignup)
            this.setSize(document.body.clientWidth, document.body.clientHeight);
    }

    protected init(): void {

        super.init();

        this.getBodyComponent().setParent(this);

    }


    ngOnInit() {

        // Subscribe on the redux store
        this.subscribeReduxStore();

        this.init();

        this.afterinit.emit();

        this.r.setStyle(this.getElDom(), 'overflow', 'hidden');

        let brixxChat = document.querySelector('#hubspot-messages-iframe-container');
        if (brixxChat !== null) {
            this.r.setStyle(brixxChat, 'z-index', `${PopupComponent.getTopestZ() - 1}`);
        }
        this.setTopest(true);

        let currentCompanyId = parseInt(localStorage.getItem('selectedCompanyId') ?? '0');
        if (this.authData.lastCompanyId && this.authData.lastCompanyId > 0)
            currentCompanyId = this.authData.lastCompanyId;
        
        if (this.companyId != 0)
            // use companyId input field instead of lastCompanyId for multi company plan view
            currentCompanyId = this.companyId;
        
        // check for Xero callback/All Organisations case 
        if (currentCompanyId < 0) { 
            let createPlanCompanyId = localStorage.getItem('createPlanCompanyId');
            if (createPlanCompanyId)
                currentCompanyId = parseInt(createPlanCompanyId);
        }
            
        this.newPlan.companyId = currentCompanyId;
    }

    onClose() {

        const body = <NewPlanFormBodyComponent>this.getBodyComponent();

        if (body.isConnectToExistingModeEnabled) {
            this.appComponent.planData.xeroConnected = false;

            this.settingsForm.getBodyComponent().onSwitchToView();
            this.close();
            this.appComponent.reloadPlanData(true);

        }
        else
            this.close();

        if (this.authData?.xeroAuthOptions?.isRedirectedFromXero)
            this.authData.xeroAuthOptions.isRedirectedFromXero = false;
    }

    setTransparentPopupStyle(): void {         
        this.r.setStyle(this.getElDom(), 'background-color', 'transparent');
    }

    showSetupWarning(): void {
        let setupDialog = <SetupWarningPopup>this.getDialogCreator().createDialog(SetupWarningPopup);

        setupDialog.setOwner(this as BaseComponent);

        const x = (document.body.clientWidth - setupDialog.width) / 2;
        const y = (document.body.clientHeight - setupDialog.height) / 2;
        this.setZIndex(PopupComponent.getTopestZ() - 1);
        setupDialog.show(x, y);

        console.log('shown ', setupDialog);

    }

    ngOnDestroy() {
        // Unsubscribe from the redux store
        this.storeUnsubscribe.next();
        this.storeUnsubscribe.complete();

        if (this.resizeUnlisten)
            this.resizeUnlisten();

        let brixxChat = document.querySelector('#hubspot-messages-iframe-container');
        if (brixxChat !== null) {
            this.r.setStyle(brixxChat, 'z-index', '');
        }
    }

    // Subscribe on the redux store
    private subscribeReduxStore(): void {
        this.appComponent$?.pipe(
            takeUntil(this.storeUnsubscribe))
            ?.subscribe(x => this.appComponent = x);

        this.settingsForm$?.pipe(
            takeUntil(this.storeUnsubscribe))
            ?.subscribe(x => this.settingsForm = x);

        this.plAccounts$?.pipe(
            takeUntil(this.storeUnsubscribe))
            ?.subscribe(x => this.plAccountsData = x);

        this.bsAccounts$?.pipe(
            takeUntil(this.storeUnsubscribe))
            ?.subscribe(x => this.bsAccountsData = x);
    }

    public handleCOASaved() {
        this.onCOASaved.emit();
    }

    onEsc() {

        const body = <NewPlanFormBodyComponent>this.getBodyComponent();

        if (body.isXeroImportModeEnabled) {
            this.showSetupWarning();
        }
        else this.close();

    }

    private getNewPlanData() {

        let newPlanData = localStorage.getItem('newPlanData');

        if (newPlanData) {
            let jNewPlanData = JSON.parse(newPlanData);
            this.monthString = jNewPlanData.month;
            this.startYearString = jNewPlanData.startYearString;
            this.actualsMonthString = jNewPlanData.actualsMonth;
            this.actualsStartYearString = jNewPlanData.actualsStartYearString;
            this.durationYear = jNewPlanData.durationYear;
            this.yearEndMonth = jNewPlanData.yearEndMonth;
            this.currencyString = jNewPlanData.currencyString;
            this.aVatText = jNewPlanData.aVatText;
            this.newPlan.planName = jNewPlanData.planName;
            this.data = jNewPlanData.importData;
        }

    }

    createPlan(failureCallback?: Function) {

        if (this.planCreateInProgress)
            return;

        this.getNewPlanData();

        this.planCreateInProgress = true;

        // Tax system: off or on
        this.newPlan.aVatCalcOption = this.aVatText === 'off' ? 0 : 1;

        // If tax system is off then default taxSystem is vat
        if (this.aVatText === 'off')
            this.newPlan.taxSystem = 'vat';
        else
            this.newPlan.taxSystem = this.aVatText; // vat, salesTax, gst

        this.newPlan.currencyId = parseInt(this.currencyString);
        this.newPlan.planStartMonth = parseInt(this.monthString);
        this.newPlan.actualsStartMonth = parseInt(this.actualsMonthString);
        this.newPlan.duration = parseInt(this.durationYear);
        this.newPlan.yearEndMonth = parseInt(this.yearEndMonth);
        //  this.newPlan.planStartYear = this.years[parseInt(this.startYearString)].value;
        
        this.newPlan.planStartYear = this.startYearString;
        this.newPlan.actualsStartYear = this.actualsStartYearString;
        
        let startDate = new Date(parseInt(this.newPlan.planStartYear), this.newPlan.planStartMonth, 1, 12);        
        let actualsStartDate = new Date(parseInt(this.newPlan.actualsStartYear), this.newPlan.actualsStartMonth, 1, 12);
        let planStartDateParam = DateUtils.getStartDateParam(startDate);
        let actualsStartDateParam = DateUtils.getStartDateParam(actualsStartDate);

        let plAccounts: AccountsGroup[] | undefined;
        let bsAccounts: AccountsGroup[] | undefined;

        if (this.plAccountsData != undefined)
            plAccounts = Object.entries(this.plAccountsData).map<AccountsGroup>(([group, accounts]) => ({ GroupName: group, Accounts: accounts }));

        if (this.bsAccountsData != undefined)
            bsAccounts = Object.entries(this.bsAccountsData).map<AccountsGroup>(([group, accounts]) => ({ GroupName: group, Accounts: accounts }));

        this.planService.createPlan(
            "blank",
            this.newPlan.planName,
            planStartDateParam,
            actualsStartDateParam,
            this.newPlan.duration,
            this.newPlan.yearEndMonth,
            this.newPlan.currencyId,
            this.newPlan.aVatCalcOption,
            this.newPlan.taxSystem,
            this.data,
            this.newPlan.companyId,
            true,
            false,
            plAccounts,
            bsAccounts
        )
            .then((data: ICreatePlanResponse | IErrorResponse) => {
                if (isSuccessResponse(data)) {
                    data = data as ICreatePlanResponse;
                    let plan: BrixxPlan = new BrixxPlan();
                    plan.planguid = data.planGUID;
                    plan.planname = this.newPlan.planName;
                    plan.planlengthName = plan.planlength + "year";
                    plan.isChoosedToKeep = data.isChoosedToKeep;

                    this.reDirectService.openPlan(plan.planguid)
                        .then((data: IErrorResponse) => {
                            if (isSuccessResponse(data)) {
                                document.location.assign(this.reDirectService.fullUrl);
                            }
                            else {
                                this.planCreateInProgress = false;
                                if (failureCallback) failureCallback();
                            }
                        }, (error) => {
                            this.planCreateInProgress = false;
                            if (failureCallback) failureCallback();
                        });

                }
                else {
                    this.planCreateInProgress = false;
                    if (failureCallback) failureCallback();
                }


            })
            .catch((error) => {
                this.planCreateInProgress = false;
                if (failureCallback) failureCallback();
            });

    }

    connectExistingPlanToXero(planGuid: string, callback: Function) {

        this.xeroService.XeroRefreshCOA(planGuid, JSON.stringify(this.plAccountsData), JSON.stringify(this.bsAccountsData)).then((res) => {

            this.appComponent.startXeroRefreshPlan(false, null, true, () => {
                this.close();
                callback();
            });

        });
    }

    enableContinue() {
        const body = <NewPlanFormBodyComponent>this.getBodyComponent();
        body.canContinue = true;
    }

}
